'use client'
import { CANARY_MISSION } from '@/enum/enum'
import { useEffect, useState, useRef } from 'react'
import SvgIcon from '../layout/svg-icons/svg-icons'
import Link from 'next/link'
import { TitleAndInput } from '../reportIndividual/inputFields'
import axios from 'axios'
import HCaptcha from '@hcaptcha/react-hcaptcha'
import { z } from 'zod'
import { ReportEventSchema, FootageType, FileWithType } from './schema'
import { convertToFormData } from './utils'
import { SuccessPage } from './successPage'
import {
  fileIcon,
  getPhotoSizeString,
  server_name,
  spinner,
  supportedImgTypes,
  trash,
  uploadSvg,
  videoIcon,
} from '../reportingTool/reportingUtils'

export interface EvidenceType {
  eventFootage: (File | FileWithType)[]
  mediaSource: string
  eventDate: string
  url: string
  location: string
  description: string
  incidentType: string
  university: string
}
type ReportEvent = z.infer<typeof ReportEventSchema>

export const ReportEvent = () => {
  const [formErrors, setFormErrors] = useState<{ [key: string]: string }>({})
  const [isDragging, setIsDragging] = useState(false)
  const [email, setEmail] = useState('')
  const [honeypotUsername, setHoneypotUsername] = useState('')
  const [collapsedStates, setCollapsedStates] = useState<{ [key: number]: boolean }>({})
  const [isLoading, setIsLoading] = useState<boolean>(false)
  const [evidence, setEvidence] = useState<EvidenceType[]>([
    {
      eventFootage: [],
      mediaSource: FootageType.Personal,
      eventDate: '',
      url: '',
      location: '',
      description: '',
      incidentType: '',
      university: '',
    },
  ])
  const [parsedData, setParsed] = useState<ReportEvent | null>(null)
  const [isSubmitted, setIsSubmitted] = useState(false)

  /* eslint-disable @typescript-eslint/no-explicit-any */
  const captchaRef = useRef<any>(null)
  const fileInputRefs = useRef<{ [key: number]: HTMLInputElement | null }>({})

  useEffect(() => {
    if (isSubmitted) {
      window.scrollTo({
        top: 0,
        behavior: 'smooth',
      })
    }
  }, [isSubmitted])

  const safeEmail = () => {
    if (email.length === 0) {
      return true
    }
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/
    return emailRegex.test(email.trim())
  }

  const onClickSubmit = () => {
    expandAll()
    const data = {
      email,
      evidence: evidence.map(
        ({ eventFootage, mediaSource, eventDate, url, location, description, incidentType }) => ({
          eventFootage,
          mediaSource,
          eventDate,
          location,
          url,
          description,
          incidentType,
        }),
      ),
    }
    const safeParsedData = ReportEventSchema.safeParse(data)
    if (!safeParsedData.success || !safeEmail()) {
      const errors: { [key: string]: string } = {}
      if (!safeEmail()) {
        errors['email'] = 'Please enter a valid email'
      }
      if (safeParsedData.error) {
        safeParsedData.error.errors.forEach((err) => {
          // eslint-disable-next-line @typescript-eslint/no-unused-vars
          const [group, index, field] = err.path
          if (field === 'url') {
            errors[`url-${index}`] = err.message
          } else {
            errors[`${field}-${index}`] = err.message
          }
        })
        setFormErrors(errors)
        setParsed(null)
        const firstErrorKey = Object.keys(errors)[0]
        if (firstErrorKey) {
          const errorElement = document.getElementsByName(firstErrorKey)?.[0]
          if (errorElement) {
            errorElement.scrollIntoView({ behavior: 'smooth', block: 'center' })
            errorElement.focus({ preventScroll: true })
          }
        }
      }
      return
    }
    setParsed(safeParsedData.data)
    captchaRef?.current?.execute()
    setIsLoading(true)
  }

  const onVerify = async (token: string) => {
    try {
      setIsLoading(true)
      if (!parsedData) return
      const formData = convertToFormData(parsedData, token)
      formData.append('username', honeypotUsername)
      if (!server_name) throw new Error('Server name is not set')
      await axios.post(`${server_name}/submit-event`, formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      })
      setIsSubmitted(true)
    } catch (error) {
      setIsLoading(false)
      let errorMessage
      let errorStatus
      let errorDetails
      if (axios.isAxiosError(error)) {
        errorStatus = error.response?.status
        errorMessage =
          error.response?.data?.message || // Server error message
          error.message || // General Axios error message
          'An unknown error occurred' // Fallback message
        errorDetails = error.response?.data?.details || ''
      } else if (error instanceof Error) {
        errorMessage = error.message
      }
      console.error('errorMessage', errorMessage)
      alert(`Error ${errorStatus}: ${errorMessage} ${errorDetails}`)
    }
  }

  const toggleCollapse = (index: number): void => {
    setCollapsedStates({
      ...collapsedStates,
      [index]: !collapsedStates[index],
    })
  }

  const collapseAll = () => {
    const collapsedStatesUpdated = Object.fromEntries(evidence.map((_, index) => [index, true]))
    setCollapsedStates(collapsedStatesUpdated)
  }

  const expandAll = () => {
    const collapsedStatesUpdated = Object.fromEntries(evidence.map((_, index) => [index, false]))
    setCollapsedStates(collapsedStatesUpdated)
  }

  const handleDragOver = (event: React.DragEvent) => {
    event.preventDefault()
    setIsDragging(true)
  }

  const handleDrop = (event: React.DragEvent, index: number) => {
    event.preventDefault()
    setIsDragging(false)

    const droppedFiles = event.dataTransfer.files
    if (droppedFiles.length > 0) {
      const validFile = supportedImgTypes.includes(droppedFiles[0].type)
      if (!validFile) {
        alert('Please enter a valid file type')
        return
      }
      const newFiles = Array.from(droppedFiles)

      setEvidence((prev) => {
        const updated = [...prev]
        updated[index] = {
          ...updated[index],
          eventFootage: [...updated[index].eventFootage, ...newFiles],
        }
        return updated
      })
    }
  }

  const handleDragLeave = () => {
    setIsDragging(false)
  }

  const handleClick = (e: React.MouseEvent<HTMLDivElement>, index: number) => {
    e.stopPropagation()
    fileInputRefs.current[index]?.click()
  }

  const handleKeyDown = (e: React.KeyboardEvent<HTMLDivElement>, index: number) => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault()
      fileInputRefs.current[index]?.click()
    }
  }

  const handleUrlChange = (e: React.ChangeEvent<HTMLInputElement>, incidentIndex: number) => {
    const { value } = e.target
    setEvidence((prev) => {
      const updated = [...prev]
      updated[incidentIndex].url = value
      return updated
    })
    setFormErrors((prevErrors) => {
      const updatedErrors = { ...prevErrors }
      delete updatedErrors[`url-${incidentIndex}`]
      return updatedErrors
    })
  }

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>, index: number) => {
    if (!e.target.files || e.target.files.length === 0) return

    const fileList = Array.from(e.target.files)

    const validFiles = fileList.filter((file) => supportedImgTypes.includes(file.type))
    setEvidence((prev) => {
      const updated = [...prev]
      updated[index] = {
        ...updated[index],
        eventFootage: [...updated[index].eventFootage, ...validFiles],
      }
      return updated
    })
  }

  const handleAddIncident = () => {
    collapseAll()
    setEvidence([
      ...evidence,
      {
        eventFootage: [],
        mediaSource: FootageType.Personal,
        eventDate: '',
        url: '',
        location: '',
        description: '',
        incidentType: '',
        university: '',
      },
    ])
  }

  const handleRemoveIncident = (index: number) => {
    const filteredInfo = evidence.filter((_, i) => i !== index)
    setEvidence(filteredInfo)

    setFormErrors((prevErrors) => {
      const newErrors: { [key: string]: string } = {}
      Object.keys(prevErrors).forEach((errorKey) => {
        const [fieldName, idxString] = errorKey.split('-')
        const idx = Number(idxString)
        if (idx === index) {
          return
        }
        if (idx > index) {
          const newKey = `${fieldName}-${idx - 1}`
          newErrors[newKey] = prevErrors[errorKey]
        } else {
          newErrors[errorKey] = prevErrors[errorKey]
        }
      })
      return newErrors
    })
  }

  const removeFile = (e: React.MouseEvent<HTMLButtonElement>, toRemove: File | FileWithType) => {
    e.preventDefault()
    e.stopPropagation()
    const updatedEventMedia = evidence.map((incident) => ({
      ...incident,
      eventFootage: incident.eventFootage.filter((file) => file !== toRemove),
    }))
    setEvidence([...updatedEventMedia])
  }

  const handleIncidentChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>,
    index: number,
  ) => {
    const { name, value } = e.target
    const updatedEventMedia = [...evidence]
    updatedEventMedia[index] = {
      ...updatedEventMedia[index],
      [name.split('-')[0]]: value,
    }
    setEvidence([...updatedEventMedia])

    const fieldKey = `${name.split('-')[0]}-${index}`

    setFormErrors((prevErrors) => {
      const updatedErrors = { ...prevErrors }
      delete updatedErrors[fieldKey]
      return updatedErrors
    })
  }

  const handleEmailChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { value } = e.target
    setEmail(value)
    setFormErrors((prevErrors) => {
      const updatedErrors = { ...prevErrors }
      delete updatedErrors['email']
      return updatedErrors
    })
  }

  const resetForm = () => {
    window.location.reload()
  }

  const renderOnlineFields = (incident: EvidenceType, index: number) => {
    return (
      <>
        <div className="flex flex-wrap md:gap-4">
          <TitleAndInput
            additionalClass={'p-15 w-fit'}
            title="Date this media was published online"
            required={true}
            type={'date'}
            inputValue={incident.eventDate}
            errorText={formErrors[`eventDate-${index}`]}
            name={`eventDate-${index}`}
            placeholder={''}
            handleFieldChange={(e) => handleIncidentChange(e, index)}
          />
          {renderEventType(incident, index)}
        </div>
        <div className="flex flex-wrap md:gap-4">
          <TitleAndInput
            title="University or Company"
            required={false}
            type="text"
            inputValue={incident.university}
            name={`university-${index}`}
            errorText={formErrors[`university-${index}`]}
            placeholder="Enter university/company"
            handleFieldChange={(e) => handleIncidentChange(e, index)}
          />
          <TitleAndInput
            title="Location"
            required={true}
            type={'string'}
            inputValue={incident.location}
            name={`location-${index}`}
            errorText={formErrors[`location-${index}`]}
            placeholder={'Enter location'}
            handleFieldChange={(e) => handleIncidentChange(e, index)}
          />
        </div>
        <div className="flex flex-wrap md:gap-4">
          <div className="flex flex-col min-w-[240px] flex-1">
            <div className="text-[16px] font-medium leading-[24px] mb-1">
              Original Media URL <span className={`text-red-700`}> *</span>
            </div>

            <div className="flex items-center mb-2">
              <input
                className={`p-2 rounded-[8px] h-[44px] flex-1 ${
                  formErrors[`url-${index}`] ? 'border-2 border-red-500' : 'border-gray-300'
                }`}
                type="url"
                value={incident.url}
                name={`url-${index}`}
                placeholder="Enter original media URL"
                onChange={(e) => handleUrlChange(e, index)}
              />
            </div>
            <div className={'text-red-500 italic text-sm'}>{formErrors[`url-${index}`]}</div>
          </div>
        </div>
      </>
    )
  }

  const renderPersonalMediaFields = (incident: EvidenceType, index: number) => {
    return (
      <>
        <div className="flex flex-wrap md:gap-4">
          <TitleAndInput
            additionalClass={'p-15 w-fit'}
            title="Date this media was filmed"
            required={true}
            type={'date'}
            inputValue={incident.eventDate}
            name={`eventDate-${index}`}
            errorText={formErrors[`eventDate-${index}`]}
            placeholder={''}
            handleFieldChange={(e) => handleIncidentChange(e, index)}
          />
          {renderEventType(incident, index)}
        </div>
        <div className="flex flex-wrap md:gap-4">
          <TitleAndInput
            title="University or Company"
            required={false}
            type="text"
            inputValue={incident.university}
            name={`university-${index}`}
            errorText={formErrors[`university-${index}`]}
            placeholder="Enter university/company"
            handleFieldChange={(e) => handleIncidentChange(e, index)}
          />
          <TitleAndInput
            title="Location"
            required={true}
            type={'string'}
            inputValue={incident.location}
            name={`location-${index}`}
            errorText={formErrors[`location-${index}`]}
            placeholder={'Enter location'}
            handleFieldChange={(e) => handleIncidentChange(e, index)}
          />
        </div>
      </>
    )
  }

  const renderEventType = (incident: EvidenceType, index: number) => {
    return (
      <div className="flex flex-col mb-1 flex-1">
        <div className="text-[16px] mb-1 font-medium leading-[24px]">
          Type of Event <span className={`text-red-700`}> *</span>
        </div>
        <select
          className={`pl-3 rounded-[8px] mb-2 h-[44px] ${
            formErrors[`incidentType-${index}`] ? 'border-red-500 border-2' : ''
          }`}
          value={incident.incidentType}
          name="incidentType"
          onChange={(e) => handleIncidentChange(e, index)}
        >
          <option value="">-- Select an option --</option>
          <option value={'encampment'}>Encampment</option>
          <option value={'harassment'}>Harassment</option>
          <option value={'protest/rally'}>Protest/Rally</option>
          <option value={'vandalism'}>Vandalism</option>
          <option value={'violence or assault'}>Violence or Assault</option>
          <option value={'other'}>Other</option>
        </select>
        <div className={'text-red-500 italic text-sm'}>{formErrors[`incidentType-${index}`]}</div>
      </div>
    )
  }

  const ChevronSvg = (isCollapsed: boolean) => {
    return (
      <SvgIcon
        width="20"
        height="20"
        fill="currentColor"
        className={`bi bi-chevron-up text-sm/6 font-bold relative xl:left-[4px] left:left-0 ${
          isCollapsed ? 'transform rotate-180' : ''
        }`}
        viewBox="0 0 16 16"
      >
        <path
          fillRule="evenodd"
          d="M7.646 4.646a.5.5 0 0 1 .708 0l6 6a.5.5 0 0 1-.708.708L8 5.707l-5.646 5.647a.5.5 0 0 1-.708-.708z"
        />
      </SvgIcon>
    )
  }

  const renderIncident = (index: number, incident: EvidenceType) => {
    const isCollapsed = collapsedStates[index] || false
    return (
      <div key={index} className="border-[1px] m-2 rounded-[8px] bg-gray-100 border-gray-600 p-4">
        <button
          className="flex focus:outline-none w-full justify-between items-center"
          onClick={() => toggleCollapse(index)}
          aria-label="Toggle Collapse"
        >
          <div className="text-[20px] font-semibold leading-[35px]">Incident #{index + 1}</div>
          <div>{ChevronSvg(isCollapsed)}</div>
        </button>
        {isCollapsed ? null : (
          <>
            <div className="text-[16px] mt-[10px] font-medium leading-[24px]">
              Incident Footage{' '}
              {evidence[index].mediaSource === FootageType.Online ? (
                <span className={'text-[12px] ml-1'}>(Optional)</span>
              ) : (
                <span className={`text-red-700`}> *</span>
              )}
            </div>
            <div className="text-[14px] font-normal leading-[21px] mb-3">
              Please upload all relevant media you have related to this incident.
            </div>
            <div
              className={`flex flex-col items-center justify-center mb:w-[600px] min-h-[215px] rounded-[8px] focus:outline-none focus:ring-2 focus:ring-blue-300 border-[2px] border-dashed 
              ${isDragging ? 'bg-blue-100 border-gray-500' : 'bg-white border-blue-300'} ${
                formErrors[`eventFootage-${index}`] ? 'border-red-500' : ''
              }`}
              onDrop={(e) => handleDrop(e, index)}
              onDragOver={handleDragOver}
              onDragLeave={handleDragLeave}
              onClick={(e) => handleClick(e, index)}
              role="button"
              tabIndex={0}
              onKeyDown={(e) => handleKeyDown(e, index)}
            >
              <div className={'m-2'}>{uploadSvg}</div>
              <div>
                <strong>Click to upload</strong> or drag and drop
              </div>
              <div className="text-[14px] font-semibold leading-[21px] mb-1">
                Max. File Size: 100MB
              </div>
              <input
                ref={(el) => {
                  fileInputRefs.current[index] = el
                }}
                type="file"
                multiple
                accept={supportedImgTypes.join(',')}
                onChange={(e) => handleFileChange(e, index)}
                className="hidden"
              />
              {formErrors[`eventFootage-${index}`] ? (
                <div className={'text-red-500 italic text-sm mt-2'}>Please upload a file</div>
              ) : null}
            </div>
            <div className={'flex flex-col mt-2'}>
              {incident.eventFootage?.map((photo, i) => {
                return (
                  <div
                    key={i}
                    className={'flex flex-row mt-1 items-center p-2 justify-between w-full'}
                  >
                    <div className="text-[14px] flex items-center break-all" title={photo.name}>
                      <div>{photo.type.startsWith('video') ? videoIcon : fileIcon}</div>
                      <div className={'flex flex-col ml-2'}>
                        <div>{photo.name}</div>
                        <div>{getPhotoSizeString(photo as File)}</div>
                      </div>
                    </div>
                    <button
                      className={`w-[24px] h-[24px] flex items-center justify-center !text-white ml-[20px] text-white`}
                      onClick={(e) => removeFile(e, incident.eventFootage[i])}
                    >
                      {trash}
                    </button>
                  </div>
                )
              })}
            </div>
            <div className="w-full m-2 text-center text-[16px] font-medium leading-[24px]">
              This media was:
            </div>
            <div className="flex space-x-4 justify-center items-center m-3 w-full">
              <div className="flex md:items-center">
                <input
                  type="radio"
                  name={`mediaSource-${index}`}
                  id="foundOnline"
                  value={FootageType.Online}
                  onChange={(e) => handleIncidentChange(e, index)}
                  checked={incident.mediaSource === FootageType.Online}
                />
                <div className="ml-[2px] md:ml-1 text-[16px] font-medium leading-[16px] md:leading-[24px]">
                  Found Online
                </div>
              </div>
              <div className="flex md:items-center">
                <input
                  type="radio"
                  name={`mediaSource-${index}`}
                  id="personalFootage"
                  value={FootageType.Personal}
                  onChange={(e) => handleIncidentChange(e, index)}
                  checked={incident.mediaSource === FootageType.Personal}
                />
                <div className="ml-[2px] md:ml-1 text-[16px] font-medium leading-[16px] md:leading-[24px]">
                  Personal Footage
                </div>
              </div>
            </div>
            {incident.mediaSource === FootageType.Online
              ? renderOnlineFields(incident, index)
              : renderPersonalMediaFields(incident, index)}
            <div className="flex flex-col mb-3">
              <div className="text-[16px] font-medium leading-[24px] mb-1">Description</div>
              <textarea
                className={`min-h-[100px] ${
                  formErrors[`description-${index}`] ? 'border-red-500 border-2' : ''
                }`}
                value={incident.description}
                onChange={(e) => handleIncidentChange(e, index)}
                id="description"
                placeholder="Enter event description"
                name={`description-${index}`}
              />
              <div className={'text-red-500 italic text-sm mt-2'}>
                {formErrors[`description-${index}`]}
              </div>
            </div>
            <div className="opacity-0 absolute top-0 left-0 h-0 w-0 -z-10">
              <div>UserName</div>
              <input
                type="text"
                id="username"
                value={honeypotUsername}
                onChange={(e) => setHoneypotUsername(e.target.value)}
                placeholder="Enter Your Username"
                name="username"
              />
            </div>
            <button
              className="bg-gray-600 mr-2 !text-white rounded-lg w-[100px] h-[30px] my-[20px] h-[40px]"
              onClick={() => toggleCollapse(index)}
            >
              Done
            </button>
            <button
              className={`bg-cm-red !text-white rounded-lg w-[100px] h-[30px] my-[20px] h-[40px] ${
                evidence.length === 1 ? 'hidden' : ''
              }`}
              onClick={() => handleRemoveIncident(index)}
            >
              Remove
            </button>
          </>
        )}
      </div>
    )
  }

  const renderAllIncidents = () => {
    if (evidence.length === 0) return
    return (
      <div className={'mx-auto'}>
        {evidence.map((incident, index) => {
          return renderIncident(index, incident)
        })}
      </div>
    )
  }

  const renderSpinner = (): JSX.Element | null => {
    if (!isLoading) return null
    return spinner
  }

  const renderHeader = () => {
    return (
      <div className="text-[30px] font-bold leading-[45px] md:text-[40px] md:leading-[60px] md:mb-[25px] border-b-[1px] border-b-gray-600">
        <div className="mb-[10px] text-center">Submission Form for Incidents and Events</div>
        <div className="text-[14px] text-center font-normal leading-[21px] mb-[5px]">
          {' '}
          For general inquiries, or ex-Canary requests, please
          <Link className="text-colorLink no-underline ml-1" href="/contact_us">
            contact us
          </Link>
          .
        </div>
        <div className="text-[14px] mb-[10px] text-center font-normal leading-[21px]">
          To report an individual, please go to
          <Link className="text-colorLink no-underline ml-1" href="/report_an_individual">
            report individual
          </Link>
          .
        </div>
      </div>
    )
  }

  const renderFormContent = () => {
    return (
      <div>
        <div className="text-[14px] font-medium leading-[21px] mb-[20px] md:text-[16px] md:leading-[24px]">
          <div className="m-2">
            Clear, unambiguous evidence of antisemitic activity is a requirement for publication on
            our website. If you have personal, unpublished media of an antisemitic incident or event
            in which you do not know the individuals involved, or if you have found media online
            which is not currently published on {CANARY_MISSION.NAME}&apos;s website, please submit
            them using the form below.
            <br />
            <br />
            If you know the individuals involved, please report them using our
            <Link className="text-colorLink no-underline ml-1" href="/report_an_individual">
              Report Individual
            </Link>{' '}
            submission tool.
          </div>
        </div>
        {renderAllIncidents()}
        <HCaptcha
          size="invisible"
          ref={captchaRef}
          sitekey={'366080e6-99da-4baa-a5a4-7b6e89f09bee'}
          onVerify={onVerify}
          onClose={() => setIsLoading(false)}
        />
        <div className={'mx-auto'}>
          <div className={'m-2 bg-gray-100 border-[1px] border-gray-600 rounded-[8px] p-4'}>
            <TitleAndInput
              title={
                'If you wish to be contacted about the status of your submission, please enter your email address'
              }
              required={false}
              type={'email'}
              inputValue={email}
              name={'email'}
              placeholder={'Enter your email address'}
              handleFieldChange={handleEmailChange}
              errorText={formErrors[`email`]}
            />
          </div>
        </div>
        <div className="flex flex-col md:flex-row justify-center w-full items-center">
          <button
            className={
              'rounded-lg flex justify-center items-center w-[200px] h-[40px] bg-gray-600 !text-white text-base font-medium leading-6 m-3'
            }
            onClick={handleAddIncident}
          >
            <div className="mr-1">Add an Incident</div>
          </button>
          <button
            className={`!text-black flex flex-row items-center justify-center bg-feYellow w-[100px] h-[40px] px-[5px] py-[3px] m-[5px] border-[1px] rounded-[8px]`}
            onClick={() => onClickSubmit()}
          >
            <div>Submit</div>
            {renderSpinner()}
          </button>
        </div>
      </div>
    )
  }

  return (
    <div className="w-full">
      {renderHeader()}
      <div className="mt-4 w-full">
        {isSubmitted ? (
          <SuccessPage submitAnother={resetForm} formType="incident" />
        ) : (
          renderFormContent()
        )}
      </div>
    </div>
  )
}
